import React, { useEffect, useId, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  getMobileGeneralDamageAnchorFromDom,
  getMobileSlotDamageAnchorFromDom,
} from '../utils/mobileBattleDamageFx';
import './RaidEnergyStreamEffect.css';

const PARTICLE_COUNT = 28;
const BEAM_LAYER_COUNT = 5;
const BASE_DURATION_MS = 920;

function resolveGeneralAnchor(playerId) {
  const anchor = getMobileGeneralDamageAnchorFromDom(playerId);
  if (!anchor) {
    throw new Error(`RaidEnergyStreamEffect: general anchor not found for playerId ${playerId}`);
  }
  return anchor;
}

function resolveUnitAnchor(zone, index, instanceId) {
  const anchor = getMobileSlotDamageAnchorFromDom(zone, index, instanceId);
  if (!anchor) {
    throw new Error(
      `RaidEnergyStreamEffect: unit anchor not found for ${zone}[${index}] instanceId=${instanceId}`
    );
  }
  return anchor;
}

function measureAnchors(fromSlot, toSlot, attempt, onReady, onGiveUp) {
  if (!fromSlot || !toSlot) {
    onGiveUp(new Error('RaidEnergyStreamEffect: fromSlot and toSlot are required'));
    return;
  }

  try {
    const fromAnchor = resolveGeneralAnchor(fromSlot.playerId);
    const toAnchor = resolveUnitAnchor(toSlot.zone, toSlot.index, toSlot.instanceId);
    onReady({
      from: { x: fromAnchor.x, y: fromAnchor.y },
      to: { x: toAnchor.x, y: toAnchor.y },
    });
  } catch (error) {
    if (attempt < 28) {
      requestAnimationFrame(() => measureAnchors(fromSlot, toSlot, attempt + 1, onReady, onGiveUp));
      return;
    }
    onGiveUp(error);
  }
}

function hashSeed(from, to, extra = 0) {
  const fx = Math.round(from.x * 13 + from.y * 7);
  const tx = Math.round(to.x * 11 + to.y * 17);
  return ((fx ^ tx ^ extra) >>> 0) || 1;
}

function createSeededRandom(seed) {
  let state = seed >>> 0;
  return () => {
    state = (Math.imul(1664525, state) + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

function buildChaoticBeamPath(from, to, rand, layerIndex) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length < 1) {
    return `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
  }

  const perpX = -dy / length;
  const perpY = dx / length;
  const chaos = length * (0.24 + layerIndex * 0.07);
  const sign = layerIndex % 2 === 0 ? 1 : -1;

  const m1x = from.x + dx * (0.28 + rand() * 0.08);
  const m1y = from.y + dy * (0.28 + rand() * 0.08);
  const m2x = from.x + dx * (0.58 + rand() * 0.1);
  const m2y = from.y + dy * (0.58 + rand() * 0.1);

  const cp1x = from.x + dx * 0.1 + perpX * chaos * sign * (0.4 + rand() * 0.9);
  const cp1y = from.y + dy * 0.1 + perpY * chaos * sign * (0.4 + rand() * 0.9);
  const cp2x = m1x + perpX * chaos * -sign * (0.55 + rand());
  const cp2y = m1y + perpY * chaos * -sign * (0.55 + rand());

  const cp3x = m1x + dx * 0.08 + perpX * chaos * sign * (0.35 + rand() * 0.8);
  const cp3y = m1y + dy * 0.08 + perpY * chaos * sign * (0.35 + rand() * 0.8);
  const cp4x = m2x + perpX * chaos * -sign * (0.5 + rand() * 1.1);
  const cp4y = m2y + perpY * chaos * -sign * (0.5 + rand() * 1.1);

  const cp5x = m2x + dx * 0.06 + perpX * chaos * sign * (0.45 + rand());
  const cp5y = m2y + dy * 0.06 + perpY * chaos * sign * (0.45 + rand());
  const cp6x = to.x - dx * 0.04 + perpX * chaos * -sign * (0.4 + rand() * 0.9);
  const cp6y = to.y - dy * 0.04 + perpY * chaos * -sign * (0.4 + rand() * 0.9);

  return [
    `M ${from.x} ${from.y}`,
    `C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${m1x} ${m1y}`,
    `C ${cp3x} ${cp3y} ${cp4x} ${cp4y} ${m2x} ${m2y}`,
    `C ${cp5x} ${cp5y} ${cp6x} ${cp6y} ${to.x} ${to.y}`,
  ].join(' ');
}

function buildStreamParticles(from, to, rand, count, durationMs) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  const perpX = length > 0 ? -dy / length : 0;
  const perpY = length > 0 ? dx / length : 1;
  const wobbleScale = Math.min(72, Math.max(28, length * 0.14));

  return Array.from({ length: count }, (_, index) => {
    const startT = rand() * 0.22;
    const travelT = 0.55 + rand() * 0.4;
    const lateral = (rand() - 0.5) * wobbleScale * (0.6 + rand() * 1.1);
    const wobbleA = (rand() - 0.5) * wobbleScale * 0.85;
    const wobbleB = (rand() - 0.5) * wobbleScale * 0.95;
    const wobbleC = (rand() - 0.5) * wobbleScale * 0.75;
    const endX = dx * travelT + perpX * lateral;
    const endY = dy * travelT + perpY * lateral;
    const sizePx = 5 + Math.round(rand() * 11);
    const variant = index % 3;

    return {
      key: `p-${index}`,
      sizePx,
      variant,
      delayMs: Math.round(startT * durationMs + rand() * durationMs * 0.35),
      durationMs: Math.round(durationMs * (0.62 + rand() * 0.45)),
      endX,
      endY,
      wobbleA,
      wobbleB,
      wobbleC,
      spinDeg: Math.round((rand() - 0.5) * 280),
    };
  });
}

/**
 * Chaotic thick red energy stream from the raided general to the raiding unit.
 */
export function RaidEnergyStreamEffect({
  fromSlot,
  toSlot,
  animationSpeedMultiplier = 1,
  onComplete,
}) {
  const gradientId = useId().replace(/:/g, '');
  const glowId = useId().replace(/:/g, '');
  const turbulenceId = useId().replace(/:/g, '');
  const [anchors, setAnchors] = useState(null);
  const [failed, setFailed] = useState(false);

  const speedFactor = animationSpeedMultiplier > 0 ? animationSpeedMultiplier : 1;
  const durationMs = Math.round(BASE_DURATION_MS / speedFactor);
  const impactDelayMs = Math.round(durationMs * 0.78);

  useLayoutEffect(() => {
    let cancelled = false;
    setAnchors(null);
    setFailed(false);

    measureAnchors(
      fromSlot,
      toSlot,
      0,
      (next) => {
        if (!cancelled) {
          setAnchors(next);
        }
      },
      (error) => {
        if (!cancelled) {
          console.error('RaidEnergyStreamEffect:', error);
          setFailed(true);
        }
      }
    );

    return () => {
      cancelled = true;
    };
  }, [fromSlot, toSlot]);

  useEffect(() => {
    if (failed) {
      if (onComplete) {
        onComplete();
      }
      return undefined;
    }
    if (!anchors) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      if (onComplete) {
        onComplete();
      }
    }, durationMs + 120);
    return () => window.clearTimeout(timer);
  }, [anchors, failed, durationMs, onComplete]);

  const streamArt = useMemo(() => {
    if (!anchors) {
      return null;
    }
    const { from, to } = anchors;
    const baseSeed = hashSeed(from, to, 0x7a1d);

    const beams = Array.from({ length: BEAM_LAYER_COUNT }, (_, layerIndex) => {
      const rand = createSeededRandom(hashSeed(from, to, layerIndex + 1));
      return {
        key: `beam-${layerIndex}`,
        pathD: buildChaoticBeamPath(from, to, rand, layerIndex),
        width: 34 - layerIndex * 4 + Math.round(rand() * 8),
        glowWidth: 52 - layerIndex * 5 + Math.round(rand() * 12),
        delayMs: Math.round(layerIndex * 18 + rand() * 40),
        opacity: 0.92 - layerIndex * 0.12,
      };
    });

    const particleRand = createSeededRandom(baseSeed);
    const particles = buildStreamParticles(from, to, particleRand, PARTICLE_COUNT, durationMs);

    return { from, to, beams, particles };
  }, [anchors, durationMs]);

  if (failed || !streamArt) {
    return null;
  }

  const { from, to, beams, particles } = streamArt;

  const layer = (
    <div className="raid-energy-stream-layer" aria-hidden>
      <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
        <defs>
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1={from.x}
            y1={from.y}
            x2={to.x}
            y2={to.y}
          >
            <stop offset="0%" stopColor="#5c0000" stopOpacity="0.55" />
            <stop offset="28%" stopColor="#8b0000" stopOpacity="0.95" />
            <stop offset="55%" stopColor="#b30000" stopOpacity="1" />
            <stop offset="82%" stopColor="#660000" stopOpacity="0.92" />
            <stop offset="100%" stopColor="#3d0000" stopOpacity="0.88" />
          </linearGradient>
          <filter id={glowId} x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="9" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id={turbulenceId} x="-60%" y="-60%" width="220%" height="220%">
            <feTurbulence type="fractalNoise" baseFrequency="0.018 0.06" numOctaves="2" seed="4" result="noise" />
            <feDisplacementMap in="SourceGraphic" in2="noise" scale="14" xChannelSelector="R" yChannelSelector="G" />
          </filter>
        </defs>

        {beams.map((beam) => (
          <g key={beam.key}>
            <path
              d={beam.pathD}
              fill="none"
              stroke={`url(#${gradientId})`}
              strokeWidth={beam.glowWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="1"
              strokeDashoffset="1"
              pathLength="1"
              className="raid-energy-stream-beam--glow"
              filter={`url(#${glowId})`}
              style={{
                animationDuration: `${durationMs}ms`,
                animationDelay: `${beam.delayMs}ms`,
                opacity: beam.opacity * 0.55,
              }}
            />
            <path
              d={beam.pathD}
              fill="none"
              stroke={`url(#${gradientId})`}
              strokeWidth={beam.width}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="1"
              strokeDashoffset="1"
              pathLength="1"
              className="raid-energy-stream-beam"
              filter={`url(#${turbulenceId})`}
              style={{
                animationDuration: `${durationMs}ms`,
                animationDelay: `${beam.delayMs}ms`,
                opacity: beam.opacity,
              }}
            />
          </g>
        ))}
      </svg>

      {particles.map((particle) => (
        <div
          key={particle.key}
          className={`raid-energy-stream-particle raid-energy-stream-particle--${particle.variant}`}
          style={{
            left: `${from.x}px`,
            top: `${from.y}px`,
            width: `${particle.sizePx}px`,
            height: `${particle.sizePx}px`,
            marginLeft: `${-particle.sizePx / 2}px`,
            marginTop: `${-particle.sizePx / 2}px`,
            '--raid-particle-duration': `${particle.durationMs}ms`,
            '--raid-particle-delay': `${particle.delayMs}ms`,
            '--raid-dx': `${particle.endX}px`,
            '--raid-dy': `${particle.endY}px`,
            '--raid-wobble-a': `${particle.wobbleA}px`,
            '--raid-wobble-b': `${particle.wobbleB}px`,
            '--raid-wobble-c': `${particle.wobbleC}px`,
            '--raid-spin': `${particle.spinDeg}deg`,
          }}
        />
      ))}

      <div
        className="raid-energy-stream-impact"
        style={{
          left: `${to.x}px`,
          top: `${to.y}px`,
          '--raid-impact-delay': `${impactDelayMs}ms`,
        }}
      />
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(layer, document.body);
  }
  return layer;
}

export default RaidEnergyStreamEffect;
